import { useState } from "react";
import { Table, Tag, Select, Button, Modal, Drawer, Form, Input, DatePicker, Popconfirm, message, Spin, Badge } from "antd";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { IIncident, TIncidentSeverity, TIncidentStatus } from "../../../types/api/incident";
import { useIncidentList, useIncidentDetail, useIncidentUpdateStatus } from "../../../hooks/api/incidents/hooks";

const SEVERITY_COLOR: Record<TIncidentSeverity, string> = {
  near_miss: "orange",
  minor: "gold",
  major: "volcano",
  fatal: "red",
};

const SEVERITY_LABEL: Record<TIncidentSeverity, string> = {
  near_miss: "Near Miss",
  minor: "Minor",
  major: "Major",
  fatal: "Fatal",
};

const STATUS_COLOR: Record<TIncidentStatus, string> = {
  open: "red",
  under_review: "orange",
  closed: "green",
};

function IncidentDetailDrawer({
  incidentId,
  onClose,
}: {
  incidentId: number | null;
  onClose: () => void;
}) {
  const [updateForm] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const { data: res, isLoading } = useIncidentDetail(incidentId);
  const incident = res?.data ?? null;

  const updateStatus = useIncidentUpdateStatus({
    onSuccess: () => { msgApi.success("Status updated."); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  if (!incidentId) return null;

  return (
    <Drawer open={!!incidentId} onClose={onClose} width={540} title="Incident Detail"
      styles={{ body: { padding: "16px 20px" } }}>
      {ctx}
      {isLoading || !incident ? (
        <div className="flex justify-center py-16"><Spin /></div>
      ) : (
        <div className="space-y-4">
          {/* header */}
          <div className="flex items-center gap-3 flex-wrap">
            <Tag color={SEVERITY_COLOR[incident.severity]} className="text-sm font-semibold">
              {SEVERITY_LABEL[incident.severity]}
            </Tag>
            <Tag color={STATUS_COLOR[incident.status]}>{incident.status.replace("_", " ")}</Tag>
            {!!incident.riddor_reportable && (
              <Tag color="red" icon={<ShieldAlert size={11} className="inline mr-0.5" />}>RIDDOR</Tag>
            )}
          </div>

          {/* key fields */}
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Date", dayjs(incident.incident_date).format("DD MMM YYYY HH:mm")],
              ["Reported By", incident.reported_by_name || "—"],
              ["Location", incident.location || "—"],
              ["Job", incident.job_number || "—"],
              ["Injury Type", incident.injury_type || "—"],
              ["Body Part", incident.body_part_affected || "—"],
            ].map(([label, val]) => (
              <div key={label} className="bg-slate-50 border border-slate-100 rounded-lg p-3">
                <p className="text-xs text-slate-400 mb-0.5">{label}</p>
                <p className="text-sm font-medium text-slate-700">{val || "—"}</p>
              </div>
            ))}
          </div>

          {/* description */}
          <div className="bg-white border border-slate-100 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-1">Description</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{incident.description}</p>
          </div>

          {incident.immediate_action && (
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
              <p className="text-xs text-amber-500 mb-1 font-semibold">Immediate Action Taken</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{incident.immediate_action}</p>
            </div>
          )}

          {incident.corrective_action && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs text-blue-500 mb-1 font-semibold">Corrective Action</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{incident.corrective_action}</p>
            </div>
          )}

          {incident.witnesses && (
            <div className="bg-white border border-slate-100 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-1">Witnesses</p>
              <p className="text-sm text-slate-700">{incident.witnesses}</p>
            </div>
          )}

          {!!incident.riddor_reportable && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
              <p className="text-xs text-red-600 font-semibold uppercase tracking-wide">RIDDOR Information</p>
              <p className="text-xs text-slate-500">Report Date: {incident.riddor_report_date ? dayjs(incident.riddor_report_date).format("DD MMM YYYY") : "Not yet submitted"}</p>
              <p className="text-xs text-slate-500">Ref: {incident.riddor_ref_number || "—"}</p>
            </div>
          )}

          {/* update status */}
          {incident.status !== "closed" && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Update Status</p>
              <Form form={updateForm} layout="vertical" size="small"
                onFinish={(vals) => {
                  updateStatus.mutate({
                    id: incident.id,
                    status: vals.status,
                    riddor_report_date: vals.riddor_report_date ? vals.riddor_report_date.format("YYYY-MM-DD") : undefined,
                    riddor_ref_number: vals.riddor_ref_number,
                  });
                }}>
                <Form.Item name="status" label="New Status" initialValue={incident.status}>
                  <Select options={[
                    { value: "open", label: "Open" },
                    { value: "under_review", label: "Under Review" },
                    { value: "closed", label: "Closed" },
                  ]} />
                </Form.Item>
                {!!incident.riddor_reportable && (
                  <div className="grid grid-cols-2 gap-x-3">
                    <Form.Item name="riddor_report_date" label="RIDDOR Report Date">
                      <DatePicker className="w-full" />
                    </Form.Item>
                    <Form.Item name="riddor_ref_number" label="RIDDOR Ref #">
                      <Input placeholder="HSE ref number" />
                    </Form.Item>
                  </div>
                )}
                <Button type="primary" htmlType="submit" loading={updateStatus.isPending} size="small">
                  Update
                </Button>
              </Form>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

const IncidentReportsPage = () => {
  const [severityFilter, setSeverityFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [riddorFilter, setRiddorFilter] = useState<boolean | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const qData = { page: 1, limit: 20, ...(severityFilter && { severity: severityFilter }), ...(statusFilter && { status: statusFilter }), ...(riddorFilter && { riddor: "true" }) };
  const { data: listRes, isLoading, refetchWithQuery } = useIncidentList(qData);
  const incidents: IIncident[] = (listRes?.data as any) ?? [];
  const total = Number(listRes?.extra?.total ?? 0);
  const riddorCount = incidents.filter((i) => !!i.riddor_reportable).length;

  const columns: ColumnType<IIncident>[] = [
    {
      title: "Date",
      dataIndex: "incident_date",
      key: "incident_date",
      render: (v) => dayjs(v).format("DD MMM YYYY"),
      width: 110,
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      render: (v: TIncidentSeverity) => <Tag color={SEVERITY_COLOR[v]}>{SEVERITY_LABEL[v]}</Tag>,
      width: 100,
    },
    {
      title: "RIDDOR",
      dataIndex: "riddor_reportable",
      key: "riddor_reportable",
      render: (v) => v ? <Tag color="red" icon={<ShieldAlert size={11} className="inline mr-0.5" />}>Yes</Tag> : <span className="text-slate-300 text-xs">—</span>,
      width: 80,
    },
    {
      title: "Reported By",
      dataIndex: "reported_by_name",
      key: "reported_by_name",
      render: (v) => v || "—",
    },
    {
      title: "Location / Job",
      key: "location",
      render: (_, r) => (
        <div>
          <div className="text-sm text-slate-700">{r.location || "—"}</div>
          {r.job_number && <div className="text-xs text-slate-400">Job {r.job_number}</div>}
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (v) => <span className="text-sm text-slate-600 line-clamp-1">{v}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: TIncidentStatus) => <Tag color={STATUS_COLOR[v]}>{v.replace("_", " ")}</Tag>,
      width: 110,
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_, r) => (
        <Button size="small" onClick={() => setSelectedId(r.id)}>View</Button>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-slate-800">Incident Reports</h1>
            {riddorCount > 0 && (
              <Badge count={`${riddorCount} RIDDOR`} style={{ background: "#dc2626" }} />
            )}
          </div>
          <p className="text-slate-500 text-sm">{total} incidents · H&S records</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Select
          placeholder="All Severities"
          value={severityFilter}
          onChange={(v) => { setSeverityFilter(v); refetchWithQuery({ ...qData, severity: v }); }}
          allowClear
          style={{ width: 150 }}
          options={[
            { value: "near_miss", label: "Near Miss" },
            { value: "minor", label: "Minor" },
            { value: "major", label: "Major" },
            { value: "fatal", label: "Fatal" },
          ]}
        />
        <Select
          placeholder="All Statuses"
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); refetchWithQuery({ ...qData, status: v }); }}
          allowClear
          style={{ width: 140 }}
          options={[
            { value: "open", label: "Open" },
            { value: "under_review", label: "Under Review" },
            { value: "closed", label: "Closed" },
          ]}
        />
        <Button
          type={riddorFilter ? "primary" : "default"}
          icon={<ShieldAlert size={14} />}
          onClick={() => {
            const next = !riddorFilter;
            setRiddorFilter(next ? true : undefined);
            refetchWithQuery({ ...qData, ...(next ? { riddor: "true" } : {}) });
          }}
          danger={!!riddorFilter}
        >
          RIDDOR Only
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          dataSource={incidents}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20, total, showTotal: (t) => `${t} incidents`, showSizeChanger: false }}
          scroll={{ x: 900 }}
          onRow={(r) => ({ onClick: () => setSelectedId(r.id), style: { cursor: "pointer" } })}
          rowClassName={(r) => r.riddor_reportable ? "bg-red-50/30" : ""}
        />
      </div>

      <IncidentDetailDrawer incidentId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
};

export default IncidentReportsPage;
